// Reading the receipts that have been on file since the first week, and turning
// only the ones that say cash into a deduction.
//
// THE PANEL SAYS WHAT EACH OUTCOME MEANS BEFORE ANY BUTTON IS PRESSED, because
// the expensive mistake here is invisible. A fuel receipt proves petrol was
// bought. It does not prove the driver paid cash. Kilomita is a card and
// FuelEasy is a card, so a fill on either is Fleetly's money, and counting it as
// his would reduce what he owes by money he never spent. Across 8,370 receipts
// that is up to R4,395,222 of goodwill funded from a deficit.
//
// So the four counts are shown with equal weight and the one that deducts is not
// the biggest number on the panel. A screen that showed only "R x ready to
// apply" would make the other four look like failures to be cleared, when three
// of them are the control working.
//
// READING AND APPLYING ARE TWO BUTTONS AND TWO ACTS. Reading spends money and
// writes proposals. Applying changes what fifty men owe, is a dry run first, and
// states what it is about to do in rand before it does it.
import { useState } from 'react'
import { AlertCircle, Banknote, FileSearch, Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { zar, num } from './cashModel'

export type Row = Record<string, any>

export function ReceiptReadPanel({
  mayRun,
  reading,
  applying,
  progress,
  lastRead,
  lastApply,
  onRead,
  onApply,
}: {
  mayRun: boolean
  reading: boolean
  applying: boolean
  progress: string | null
  lastRead: Row | null
  lastApply: Row | null
  onRead: (batches: number) => void
  onApply: (dryRun: boolean, tolerance: number) => void
}) {
  const [batches, setBatches] = useState(4)
  const [tolerance, setTolerance] = useState(1)

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div>
        <h3 className="text-sm font-medium text-foreground">Receipts already on file</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Every shift submission since February 2025 carried a photograph of the petrol receipt, and
          none of them has ever been read. Reading one asks it a single question: does the slip say
          the driver paid cash. Only cash deducts. A card slip deducts nothing, because whose card
          it was has not been established: a Fleetly account and a driver swiping his own card look
          identical on the slip. That is a separate piece of work and it is not this one.
        </p>
      </div>

      {!mayRun ? (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Reading the whole fleet&rsquo;s documents needs the same permission as loading a bank
          statement. Whoever holds that can run it.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Batches of six</Label>
              <Input
                type="number"
                min={1}
                max={40}
                className="h-8 w-24 text-xs"
                value={batches}
                onChange={(e) => setBatches(Math.min(Math.max(Number(e.target.value) || 1, 1), 40))}
              />
            </div>
            <Button type="button" size="sm" disabled={reading} onClick={() => onRead(batches)}>
              {reading ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileSearch className="mr-1.5 h-3.5 w-3.5" />
              )}
              Read {batches * 6} receipts
            </Button>
            <span className="text-xs text-muted-foreground">
              Six at a time, because each one is fetched and read and the whole cockpit shares one
              connection pool.
            </span>
          </div>

          {progress && <p className="text-xs text-muted-foreground">{progress}</p>}

          {lastRead && (
            <p className="text-xs text-muted-foreground">
              {lastRead.ok === false ? (
                <span className="text-destructive">{String(lastRead.error ?? 'The read failed.')}</span>
              ) : (
                <>
                  {num(lastRead.read)} read, {num(lastRead.said_cash)} said cash,{' '}
                  {num(lastRead.failed)} could not be read.{' '}
                  {lastRead.remaining !== null && lastRead.remaining !== undefined
                    ? `${num(lastRead.remaining)} still on the pile.`
                    : ''}
                </>
              )}
            </p>
          )}
        </>
      )}

      {lastApply && lastApply.ok !== false && (
        <div className="space-y-2 rounded-md border border-border bg-muted/30 p-2">
          <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-6">
            <div>
              <span className="text-muted-foreground">Says cash and ties</span>
              <div className="tabular-nums font-medium">
                {num(lastApply.ready)} · {zar(lastApply.zar)}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Card, whose is open</span>
              <div className="tabular-nums">{num(lastApply.left_card)}</div>
            </div>
            <div>
              <span className="text-muted-foreground">FuelEasy NFC tag</span>
              <div className="tabular-nums">{num(lastApply.left_fuel_easy)}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Tender unclear</span>
              <div className="tabular-nums">{num(lastApply.left_tender_unclear)}</div>
            </div>
            <div>
              {/* The RAND is here and not only the count. Four slips saying cash
                  for less than was declared is a number to go and ask about, and
                  a bare "4" does not tell anybody how much to ask about. */}
              <span className="text-muted-foreground">Says cash, does not tie</span>
              <div className="tabular-nums">
                {num(lastApply.left_amount_disagrees)} · {zar(lastApply.left_amount_disagrees_zar)}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Could not be read</span>
              <div className="tabular-nums">{num(lastApply.left_unreadable)}</div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{String(lastApply.note ?? '')}</p>
        </div>
      )}

      {mayRun && (
        <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
          <div className="space-y-1">
            <Label className="text-xs">Rounding allowance</Label>
            <Input
              type="number"
              min={0}
              max={50}
              className="h-8 w-24 text-xs"
              value={tolerance}
              onChange={(e) => setTolerance(Math.min(Math.max(Number(e.target.value) || 0, 0), 50))}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={applying}
            onClick={() => onApply(true, tolerance)}
          >
            Show me what would change
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={applying || !lastApply || Number(lastApply?.ready ?? 0) === 0}
            onClick={() => onApply(false, tolerance)}
          >
            <Banknote className="mr-1.5 h-3.5 w-3.5" />
            Deduct the {num(lastApply?.ready ?? 0)} cash receipts
          </Button>
          <span className="text-xs text-muted-foreground">
            Nothing is deducted until you have seen the figure above and pressed the second button.
          </span>
        </div>
      )}
    </div>
  )
}
